import { useState, useRef, useEffect, useLayoutEffect, useContext, useMemo, useCallback } from 'react';
import { AuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faArrowUp, faCircleStop, faClose, faPlus, faPaperclip, faFileAlt, faFilePowerpoint, faDownload, faMicrophone, faMagnifyingGlass, faPenToSquare, faComment } from '@fortawesome/free-solid-svg-icons';
import MarkdownIt from 'markdown-it';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import EliaTableGraph from './EliaTableGraph';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { buildSummaryRequest, EliaSummaryAction } from './EliaSummaryPanel';
import EliaButton from "../EliaButton";

const buildAgentMessage = (message) => `${message}`;
const NEW_CONVERSATION_KEY = "__elia_new_conversation__";
const getConversationIdFromHeaders = (headers) =>
  headers.get("X-Conversation-Id") || "";

const normalizeBaseUrl = (url) => {
  if (!url) return "";
  return url.endsWith("/") ? url : `${url}/`;
};

const buildApiUrl = (baseUrl, path) =>
  `${normalizeBaseUrl(baseUrl)}${String(path).replace(/^\/+/, "")}`;

const isFetchNetworkError = (error) =>
  error instanceof TypeError && /failed to fetch/i.test(error.message);

const WELCOME_BENCHMARK_QUESTIONS = [
  {
    label: "Tickets",
    prompt: "Quels sont les 5 derniers tickets ouverts ?",
    icon: faFileAlt,
    action: "prefill",
  },
  {
    label: "Abonnements",
    prompt: "Quels sont mes abonnements qui arrivent à échéance cette année ?",
    icon: faMagnifyingGlass,
    action: "prefill",
  },
  {
    label: "Analyse",
    prompt: "Analyse moi ma configuration de mes Firewall",
    icon: faPaperclip,
    action: "prefill",
  },
];

const getWelcomeGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Bonjour";
  if (hour < 18) return "Bon apr\u00e8s-midi";
  return "Bonsoir";
};

const extractArrayPayload = (payload, keys) => {
  if (Array.isArray(payload)) return payload;

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  }

  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getConversationId = (conversation) => {
  const id = conversation?.conversation_id
    || conversation?.conversationId
    || conversation?.id
    || conversation?._id
    || conversation?.data?.conversation_id
    || conversation?.data?.conversationId
    || conversation?.data?.id
    || conversation?.metadata?.conversation_id
    || conversation?.metadata?.conversationId
    || conversation?.metadata?.id
    || conversation?.session?.conversation_id
    || conversation?.session?.conversationId
    || conversation?.session?.id
    || conversation?.conversation?.conversation_id
    || conversation?.conversation?.conversationId
    || conversation?.conversation?.id
    || "";

  return id ? String(id) : "";
};

const getMessageDate = (message) =>
  message?.date
  || message?.created_at
  || message?.createdAt
  || message?.updated_at
  || message?.updatedAt
  || "";

const normalizeRole = (value) => {
  const role = String(value || "").toLowerCase();
  return role === "user" ? "user" : "assistant";
};

const parseStoredStreamEvents = (content) => {
  if (typeof content !== "string" || !content.includes('"type"')) return [];

  const events = [];
  let startIndex = -1;
  let depth = 0;
  let isInString = false;
  let isEscaped = false;

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];

    if (isInString) {
      if (isEscaped) {
        isEscaped = false;
      } else if (char === "\\") {
        isEscaped = true;
      } else if (char === '"') {
        isInString = false;
      }
      continue;
    }

    if (char === '"') {
      isInString = true;
    } else if (char === "{") {
      if (depth === 0) startIndex = index;
      depth += 1;
    } else if (char === "}") {
      depth -= 1;

      if (depth === 0 && startIndex >= 0) {
        try {
          events.push(JSON.parse(content.slice(startIndex, index + 1)));
        } catch {
          return [];
        }
        startIndex = -1;
      }
    }
  }

  return events;
};

const normalizeStoredMessageContent = (content, role) => {
  const rawContent = content || "";
  if (role !== "assistant") return rawContent;

  const events = parseStoredStreamEvents(rawContent);
  if (!events.length) return rawContent;

  const finalEvent = [...events]
    .reverse()
    .find((event) => event?.type === "final" && typeof event.text === "string");

  if (finalEvent) return finalEvent.text;

  const deltaText = events
    .filter((event) => event?.type === "delta" && typeof event.text === "string")
    .map((event) => event.text)
    .join("");

  return deltaText || rawContent;
};

const isInternalPptxRequest = (content) => {
  const text = String(content || "");

  return (
    text.includes("PPTX_DOWNLOAD_URL:")
    && text.includes("Contenu source")
    && text.includes("PowerPoint")
  ) || (
    text.startsWith("Tu es un expert en cr")
    && text.includes("PowerPoint")
  );
};

const normalizeConversation = (conversation) => {
  const id = getConversationId(conversation);
  const title = conversation?.title
    || conversation?.name
    || conversation?.label
    || conversation?.last_message
    || conversation?.lastMessage
    || "Nouvelle conversation";
  const updatedAt = conversation?.updated_at
    || conversation?.updatedAt
    || conversation?.last_message_at
    || conversation?.lastMessageAt
    || conversation?.created_at
    || conversation?.createdAt
    || "";

  return {
    id,
    title: String(title).slice(0, 90),
    updatedAt,
  };
};

const normalizeMessage = (message) => {
  const role = normalizeRole(message?.type || message?.role);
  const content = message?.content || message?.message || message?.text || "";

  return {
    role,
    content: normalizeStoredMessageContent(content, role),
    date: getMessageDate(message),
  };
};

const PPTX_LINK_REGEX = /(https?:\/\/[^\s<>"']+\.pptx(?:\?[^\s<>"']*)?)/i;
const PPTX_LINK_GLOBAL_REGEX = /(https?:\/\/[^\s<>"']+\.pptx(?:\?[^\s<>"']*)?)/gi;
const EXCEL_LINK_GLOBAL_REGEX = /(https?:\/\/[^\s<>"']+\.xlsx(?:\?[^\s<>"']*)?)/gi;

const getPptxLink = (content) => {
  const normalizedContent = content
    ?.replace(/\\\//g, "/")
    .replace(/\\u0026/gi, "&");
  const match = normalizedContent?.match(PPTX_LINK_REGEX);
  return match ? match[1].replace(/[)\].,;]+$/, "") : "";
};

const getPptxLinks = (content) => {
  const normalizedContent = content
    ?.replace(/\\\//g, "/")
    .replace(/\\u0026/gi, "&");

  return [...(normalizedContent?.matchAll(PPTX_LINK_GLOBAL_REGEX) || [])]
    .map((match) => match[1].replace(/[)\].,;]+$/, ""));
};

const getExcelLinks = (content) => {
  const normalizedContent = content
    ?.replace(/\\\//g, "/")
    .replace(/\\u0026/gi, "&");

  return [...new Set([...(normalizedContent?.matchAll(EXCEL_LINK_GLOBAL_REGEX) || [])]
    .map((match) => match[1].replace(/[)\].,;]+$/, "")))];
};

const getFileNameFromUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return decodeURIComponent(parsedUrl.pathname.split("/").pop() || "presentation.pptx");
  } catch {
    return "presentation.pptx";
  }
};

const isSignedUrlExpired = (url) => {
  try {
    const expiration = Number(new URL(url).searchParams.get("exp"));
    return Number.isFinite(expiration) && expiration * 1000 <= Date.now();
  } catch {
    return false;
  }
};

const formatHistoryDate = (value) => {
  if (!value) return "";
  const normalizedValue = typeof value === "string" ? value.replace(" ", "T") : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getDateOnlyTime = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const getShortMonthLabel = (date) => {
  const month = new Intl.DateTimeFormat("fr-FR", { month: "long" }).format(date);
  return month.length > 4 ? `${month.slice(0, 4)}.` : month;
};

const formatConversationDate = (value) => {
  const normalizedValue = typeof value === "string" ? value.replace(" ", "T") : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const hourDiff = Math.max(0, Math.floor(diffMs / 3600000));
  const dayDiff = Math.max(0, Math.floor((getDateOnlyTime(now) - getDateOnlyTime(date)) / 86400000));

  if (dayDiff === 0) {
    const hours = Math.max(1, hourDiff);
    return `Il y a ${hours} heure${hours > 1 ? "s" : ""}`;
  }

  if (dayDiff <= 7) {
    return `Il y a ${dayDiff} jour${dayDiff > 1 ? "s" : ""}`;
  }

  const day = date.getDate();
  const month = getShortMonthLabel(date);

  if (date.getFullYear() === now.getFullYear()) {
    return `${day} ${month}`;
  }

  return `${day} ${month} ${date.getFullYear()}`;
};

const getBackendFailureMessage = (content) => {
  if (!content) return "";

  if (/maximum execution time/i.test(content)) {
    return "Timeout backend pendant la génération PowerPoint.";
  }

  if (/"status"\s*:\s*500/.test(content) || /HTTP\s*500/i.test(content) || /An error occurred/i.test(content)) {
    return "Erreur backend pendant la génération PowerPoint.";
  }

  return "";
};

const getStableKey = (value) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return String(hash);
};

const downloadFile = (url) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const isRecoverableJsonParseError = (error) =>
  error instanceof SyntaxError
  && /unterminated string|unexpected end|bad control character/i.test(error.message);

const cleanMarkdownText = (text) =>
  text
    .replace(/\*\*/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^#+\s*/, "")
    .replace(/^["']+|["']+$/g, "")
    .trim();

const normalizeDetectionText = (text) =>
  cleanMarkdownText(text)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getConfigurationName = (content) => {
  if (!content) return "";
  const firstMeaningfulLine = content
    .split("\n")
    .map(cleanMarkdownText)
    .find((line) => line.length > 0);

  const match = firstMeaningfulLine?.match(/^Configuration\s*:\s*(.+)$/i);
  if (match) return cleanMarkdownText(match[1]);

  const firstSentence = firstMeaningfulLine?.split(/[.!?]/)[0] || "";
  const normalizedFirstSentence = normalizeDetectionText(firstSentence);
  const hasSummaryTrigger = ["voici", "configuration", "analyse"].every((keyword) =>
    normalizedFirstSentence.includes(keyword)
  );

  return hasSummaryTrigger ? "Configuration" : "";
};

const cleanTableCell = (cell) =>
  cell
    .trim()
    .replace(/\*\*/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1");

const isMarkdownTableSeparator = (line) =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const splitMarkdownTableRow = (line) => {
  const trimmedLine = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmedLine.split("|").map(cleanTableCell);
};

const normalizeColumnKey = (label, index) =>
  label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "") || `col_${index}`;

const buildTableGraphFromMarkdown = (tableLines) => {
  const headers = splitMarkdownTableRow(tableLines[0]);
  const columns = headers.map((header, index) => {
    const key = normalizeColumnKey(header, index);
    const normalizedHeader = normalizeColumnKey(header, index);

    return {
      key,
      label: header,
      type: normalizedHeader.includes("criticite") || normalizedHeader.includes("statut") ? "status" : "text",
    };
  });

  const rows = tableLines.slice(2).map((line, rowIndex) => {
    const cells = splitMarkdownTableRow(line);

    return columns.reduce((row, column, columnIndex) => ({
      ...row,
      [column.key]: cells[columnIndex] || "",
    }), { __eliaRowKey: `markdown-row-${rowIndex}` });
  });

  return {
    source: "markdown",
    columns,
    rows,
  };
};

const parseMarkdownTables = (content) => {
  const lines = content.split("\n");
  const parts = [];
  const markdownBuffer = [];
  let index = 0;

  const flushMarkdown = () => {
    if (!markdownBuffer.length) return;
    parts.push({ type: "markdown", content: markdownBuffer.join("\n") });
    markdownBuffer.length = 0;
  };

  while (index < lines.length) {
    const line = lines[index];
    const nextLine = lines[index + 1] || "";

    if (line.includes("|") && isMarkdownTableSeparator(nextLine)) {
      const tableLines = [line, nextLine];
      index += 2;

      while (index < lines.length && lines[index].includes("|") && lines[index].trim()) {
        tableLines.push(lines[index]);
        index += 1;
      }

      if (tableLines.length > 2) {
        flushMarkdown();
        parts.push({ type: "tableGraph", data: buildTableGraphFromMarkdown(tableLines) });
        continue;
      }

      markdownBuffer.push(...tableLines);
      continue;
    }

    markdownBuffer.push(line);
    index += 1;
  }

  flushMarkdown();
  return parts;
};

const parseMessageContent = (content) => {
  if (!content) return [{ type: 'markdown', content: '' }];

  const parts = [];
  const blockRegex = /```elia-table-graph\s*([\s\S]*?)```/gi;
  let lastIndex = 0;
  let match;

  while ((match = blockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...parseMarkdownTables(content.slice(lastIndex, match.index)));
    }

    try {
      parts.push({ type: 'tableGraph', data: JSON.parse(match[1].trim()) });
    } catch {
      parts.push({ type: 'markdown', content: match[0] });
    }

    lastIndex = blockRegex.lastIndex;
  }

  if (lastIndex < content.length) {
    parts.push(...parseMarkdownTables(content.slice(lastIndex)));
  }

  return parts.length ? parts : [{ type: 'markdown', content }];
};

const splitMarkdownExcelActions = (content) => {
  const lines = String(content || "").split("\n");
  const parts = [];
  const markdownBuffer = [];

  const flushMarkdown = () => {
    if (!markdownBuffer.length) return;
    parts.push({ type: "markdown", content: markdownBuffer.join("\n") });
    markdownBuffer.length = 0;
  };

  lines.forEach((line) => {
    const urls = getExcelLinks(line);

    if (!urls.length) {
      markdownBuffer.push(line);
      return;
    }

    flushMarkdown();
    const label = cleanMarkdownText(line.replace(EXCEL_LINK_GLOBAL_REGEX, "").replace(/^[\s:–—-]+|[\s:–—-]+$/g, ""));
    urls.forEach((url) => {
      parts.push({
        type: "excelAction",
        url,
        title: label || "Excel",
      });
    });
  });

  flushMarkdown();
  return parts;
};

function EliaExcelAction({ title, url }) {
  const isExpired = isSignedUrlExpired(url);

  return (
    <div className="elia-summary-action elia-file-action excel">
      <div className="elia-summary-action-icon" aria-hidden="true">
        <EliaButton status="active" />
      </div>
      <div className="elia-summary-action-content">
        <div className="elia-summary-action-title">{title}</div>
        <div className="elia-summary-action-subtitle">
          {isExpired ? "Lien expiré" : getFileNameFromUrl(url)}
        </div>
      </div>
      {isExpired ? (
        <button type="button" className="elia-summary-action-button" disabled title="Ce lien de téléchargement a expiré">
          Lien expiré
        </button>
      ) : (
        <a className="elia-summary-action-button" href={url} target="_blank" rel="noopener noreferrer">
          Télécharger
        </a>
      )}
    </div>
  );
}

const RightSidebar = ({ isOpen, onClose, isExpanded, setIsExpanded, isLeftSidebarOpen }) => {
  const [, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 945px)").matches : false
  );
  const { apiUrl, user } = useContext(AuthContext);
  const eliaApiUrl = useMemo(
    () => normalizeBaseUrl(import.meta.env.VITE_ELIA_API_URL || apiUrl),
    [apiUrl]
  );

  const [messages, setMessages] = useState([]);
  const { theme } = useTheme();
  const [input, setInput] = useState('');
  const [selectedXmlFile, setSelectedXmlFile] = useState(null);
  const [isImportMenuOpen, setIsImportMenuOpen] = useState(false);
  const [isConfigUploading, setIsConfigUploading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [activeConversationKey, setActiveConversationKey] = useState(NEW_CONVERSATION_KEY);
  const [, setMessagesByConversation] = useState(() => ({
    [NEW_CONVERSATION_KEY]: [],
  }));
  const [loadingConversationKeys, setLoadingConversationKeys] = useState({});
  const [loadingMessageConversationKeys, setLoadingMessageConversationKeys] = useState({});
  const [isConversationSidebarOpen, setIsConversationSidebarOpen] = useState(false);
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [isConversationSearchOpen, setIsConversationSearchOpen] = useState(false);
  const [conversationSearchQuery, setConversationSearchQuery] = useState("");
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const {
    transcript,
    listening: isVoiceListening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();
  const inputRef = useRef(null);
  const isLoading = Boolean(Object.keys(loadingConversationKeys[activeConversationKey] || {}).length);
  const isConversationMessagesLoading = Boolean(loadingMessageConversationKeys[activeConversationKey]);
  const activeConversationIdRef = useRef(null);
  const activeConversationKeyRef = useRef(NEW_CONVERSATION_KEY);
  const messagesByConversationRef = useRef({
    [NEW_CONVERSATION_KEY]: [],
  });
  const conversationsRequestRef = useRef(null);
  const hasLoadedConversationsRef = useRef(false);
  const shouldRefreshConversationsRef = useRef(false);
  const conversationsApiUrlRef = useRef("");
  const conversationMessagesRequestsRef = useRef(new Map());
  const abortControllersRef = useRef(new Map());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const importMenuRef = useRef(null);
  const pptxInFlightRef = useRef(new Set());
  const voiceNoSpeechTimeoutRef = useRef(null);
  const voiceSilenceTimeoutRef = useRef(null);
  const voiceTranscriptRef = useRef("");
  const voiceSubmitGuardRef = useRef(false);
  const lastOpenLayoutRef = useRef({
    isExpanded: false,
    isConversationPanelVisible: false,
  });
  const [pptxDownloads, setPptxDownloads] = useState({});
  const [isFileHistoryOpen, setIsFileHistoryOpen] = useState(false);

  const readyPptxFiles = useMemo(() => {
    const filesByUrl = new Map();

    Object.entries(pptxDownloads).forEach(([downloadKey, fileState]) => {
      if (!fileState?.url) return;

      filesByUrl.set(fileState.url, {
        id: downloadKey,
        url: fileState.url,
        name: fileState.name || getFileNameFromUrl(fileState.url),
        configurationName: fileState.configurationName || "PowerPoint",
        createdAt: fileState.createdAt || "",
        source: "cache",
      });
    });

    messages.forEach((message, messageIndex) => {
      getPptxLinks(message.content).forEach((url, urlIndex) => {
        if (filesByUrl.has(url)) return;

        filesByUrl.set(url, {
          id: `message-${messageIndex}-${urlIndex}`,
          url,
          name: getFileNameFromUrl(url),
          configurationName: getConfigurationName(message.content) || "PowerPoint",
          createdAt: "",
          source: "message",
        });
      });
    });

    return Array.from(filesByUrl.values()).sort((a, b) =>
      String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
    );
  }, [messages, pptxDownloads]);

  const filteredConversations = useMemo(() => {
    const normalizedQuery = conversationSearchQuery.trim().toLowerCase();

    return conversations.filter((conversation) => {
      if (!normalizedQuery) return true;
      return conversation.title.toLowerCase().includes(normalizedQuery);
    });
  }, [conversationSearchQuery, conversations]);

  const recentConversations = useMemo(
    () => conversations.slice(0, 10),
    [conversations]
  );

  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  });

  const formatMessage = (content) => {
    if (!content) return "";
    return md.render(content);
  };

  const renderMessageParts = (parts) =>
    parts.flatMap((part, partIndex) => {
      if (part.type === 'tableGraph') {
        return <EliaTableGraph key={`table-graph-${partIndex}`} data={part.data} />;
      }

      return splitMarkdownExcelActions(part.content).map((markdownPart, markdownPartIndex) => {
        if (markdownPart.type === "excelAction") {
          return (
            <EliaExcelAction
              key={`excel-action-${partIndex}-${markdownPartIndex}`}
              title={markdownPart.title}
              url={markdownPart.url}
            />
          );
        }

        return (
          <div
            key={`markdown-${partIndex}-${markdownPartIndex}`}
            className="message-markdown"
            dangerouslySetInnerHTML={{ __html: formatMessage(markdownPart.content) }}
          />
        );
      });
    });

  const setConversationMessages = (conversationKey, updater) => {
    const currentMessages = messagesByConversationRef.current[conversationKey] || [];
    const nextMessages = typeof updater === "function" ? updater(currentMessages) : updater;
    const nextCache = {
      ...messagesByConversationRef.current,
      [conversationKey]: nextMessages,
    };

    messagesByConversationRef.current = nextCache;
    setMessagesByConversation(nextCache);

    if (activeConversationKeyRef.current === conversationKey) {
      setMessages(nextMessages);
    }
  };

  const renameConversationCacheKey = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;

    const currentCache = messagesByConversationRef.current;
    const messagesToMove = currentCache[fromKey];
    if (!messagesToMove) {
      if (activeConversationKeyRef.current === fromKey) {
        activeConversationKeyRef.current = toKey;
        activeConversationIdRef.current = toKey;
        setActiveConversationKey(toKey);
        setActiveConversationId(toKey);
      }
      return;
    }

    const nextCache = { ...currentCache, [toKey]: messagesToMove };
    delete nextCache[fromKey];
    messagesByConversationRef.current = nextCache;
    setMessagesByConversation(nextCache);

    if (activeConversationKeyRef.current === fromKey) {
      activeConversationKeyRef.current = toKey;
      activeConversationIdRef.current = toKey;
      setActiveConversationKey(toKey);
      setActiveConversationId(toKey);
      setMessages(messagesToMove);
    }
  };

  const setConversationLoading = (conversationKey, requestKey, isConversationLoading) => {
    setLoadingConversationKeys((prev) => {
      if (isConversationLoading) {
        return {
          ...prev,
          [conversationKey]: {
            ...(prev[conversationKey] || {}),
            [requestKey]: true,
          },
        };
      }

      const nextRequests = { ...(prev[conversationKey] || {}) };
      delete nextRequests[requestKey];

      const next = { ...prev, [conversationKey]: nextRequests };
      if (Object.keys(nextRequests).length === 0) {
        delete next[conversationKey];
      }

      return next;
    });
  };

  const setConversationMessagesLoading = (conversationKey, isConversationMessagesLoading) => {
    setLoadingMessageConversationKeys((prev) => {
      if (isConversationMessagesLoading) {
        return { ...prev, [conversationKey]: true };
      }

      const next = { ...prev };
      delete next[conversationKey];
      return next;
    });
  };

  const fetchConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId || !eliaApiUrl) return [];

    const existingRequest = conversationMessagesRequestsRef.current.get(conversationId);
    if (existingRequest) return existingRequest;

    const request = (async () => {
      const response = await fetch(buildApiUrl(eliaApiUrl, `agentic/conversation/${encodeURIComponent(conversationId)}`), {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const normalizedMessages = extractArrayPayload(payload, ["messages", "items", "results"])
        .map(normalizeMessage)
        .filter((message) => message.content);
      return normalizedMessages;
    })();

    conversationMessagesRequestsRef.current.set(conversationId, request);

    try {
      return await request;
    } finally {
      if (conversationMessagesRequestsRef.current.get(conversationId) === request) {
        conversationMessagesRequestsRef.current.delete(conversationId);
      }
    }
  }, [eliaApiUrl]);

  const prefetchConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId || !eliaApiUrl) return;
    if (messagesByConversationRef.current[conversationId]) return;

    try {
      const nextMessages = await fetchConversationMessages(conversationId);

      if (messagesByConversationRef.current[conversationId]) return;

      const nextCache = {
        ...messagesByConversationRef.current,
        [conversationId]: nextMessages,
      };

      messagesByConversationRef.current = nextCache;
      setMessagesByConversation(nextCache);

      if (activeConversationKeyRef.current === conversationId) {
        setMessages(nextMessages);
      }
    } catch (error) {
      if (!isFetchNetworkError(error)) {
        console.error("Erreur prechargement conversation:", error);
      }
    }
  }, [eliaApiUrl, fetchConversationMessages]);

  const loadConversations = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!eliaApiUrl) return false;

    if (conversationsApiUrlRef.current !== eliaApiUrl) {
      conversationsApiUrlRef.current = eliaApiUrl;
      hasLoadedConversationsRef.current = false;
    }

    if (hasLoadedConversationsRef.current && !force) return true;

    if (conversationsRequestRef.current) {
      if (!silent) setIsConversationsLoading(true);
      try {
        await conversationsRequestRef.current;
      } finally {
        if (!silent) setIsConversationsLoading(false);
      }
      if (!force) return true;
    }

    if (!silent) setIsConversationsLoading(true);

    const request = (async () => {
      const response = await fetch(buildApiUrl(eliaApiUrl, "agentic/conversations"), {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      return extractArrayPayload(payload, ["conversations", "items", "results"])
        .map(normalizeConversation)
        .filter((conversation) => conversation.id && !isInternalPptxRequest(conversation.title));
    })();

    conversationsRequestRef.current = request;

    try {
      const normalizedConversations = await request;
      setConversations(normalizedConversations);
      hasLoadedConversationsRef.current = true;
      normalizedConversations.slice(0, 10).forEach((conversation) => {
        void prefetchConversationMessages(conversation.id);
      });
      return true;
    } catch (error) {
      if (!silent) {
        if (!isFetchNetworkError(error)) {
          console.error("Erreur chargement conversations:", error);
        }
        setConversations([]);
      }
      return false;
    } finally {
      if (conversationsRequestRef.current === request) {
        conversationsRequestRef.current = null;
      }
      if (!silent) setIsConversationsLoading(false);
    }
  }, [eliaApiUrl, prefetchConversationMessages]);

  const refreshConversationsIfNeeded = useCallback(async ({ silent = true } = {}) => {
    if (!shouldRefreshConversationsRef.current) return false;

    const refreshed = await loadConversations({ silent, force: true });
    if (refreshed) {
      shouldRefreshConversationsRef.current = false;
    }
    return refreshed;
  }, [loadConversations]);

  const loadConversationMessages = async (conversationId) => {
    if (!conversationId) return;

    setActiveConversationId(conversationId);
    setActiveConversationKey(conversationId);
    activeConversationIdRef.current = conversationId;
    activeConversationKeyRef.current = conversationId;
    setInput("");
    resetTextarea();

    const cachedMessages = messagesByConversationRef.current[conversationId];
    if (cachedMessages) {
      setMessages(cachedMessages);
      return;
    }

    setMessages([]);

    setConversationMessagesLoading(conversationId, true);

    try {
      const nextMessages = await fetchConversationMessages(conversationId);
      setConversationMessages(conversationId, nextMessages);
    } catch (error) {
      console.error("Erreur chargement messages conversation:", error);
      setConversationMessages(conversationId, []);
    } finally {
      setConversationMessagesLoading(conversationId, false);
    }
  };

  const startNewConversation = () => {
    void refreshConversationsIfNeeded({ silent: true });
    activeConversationKeyRef.current = NEW_CONVERSATION_KEY;
    activeConversationIdRef.current = null;
    setActiveConversationId(null);
    setActiveConversationKey(NEW_CONVERSATION_KEY);
    setConversationMessages(NEW_CONVERSATION_KEY, []);
    setIsConversationSearchOpen(false);
    setConversationSearchQuery("");
    setInput("");
    resetTextarea();
  };

  const openConversationSearch = () => {
    setIsConversationSearchOpen(true);
    setConversationSearchQuery("");
    if (shouldRefreshConversationsRef.current) {
      void refreshConversationsIfNeeded({ silent: false });
      return;
    }
    loadConversations();
  };

  const closeConversationSearch = () => {
    setIsConversationSearchOpen(false);
    setConversationSearchQuery("");
  };

  const selectConversation = (conversationId) => {
    closeConversationSearch();
    void refreshConversationsIfNeeded({ silent: true });
    loadConversationMessages(conversationId);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resizeInput = () => {
    const textarea = inputRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const maxHeight = 150;
    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + "px";
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  const clearVoiceTimers = () => {
    if (voiceNoSpeechTimeoutRef.current) {
      clearTimeout(voiceNoSpeechTimeoutRef.current);
      voiceNoSpeechTimeoutRef.current = null;
    }

    if (voiceSilenceTimeoutRef.current) {
      clearTimeout(voiceSilenceTimeoutRef.current);
      voiceSilenceTimeoutRef.current = null;
    }
  };

  const stopVoiceInput = ({ keepText = true } = {}) => {
    clearVoiceTimers();
    SpeechRecognition.stopListening();
    setIsVoiceMode(false);

    const voiceText = voiceTranscriptRef.current.trim();
    if (keepText && voiceText) {
      setInput(voiceTranscriptRef.current);
      window.requestAnimationFrame(resizeInput);
      return;
    }

    resetTranscript();
    voiceTranscriptRef.current = "";
    setInput("");
    window.requestAnimationFrame(resetTextarea);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    activeConversationKeyRef.current = activeConversationKey;
  }, [activeConversationId, activeConversationKey]);

  useEffect(() => {
    if (activeConversationId) {
      activeConversationIdRef.current = activeConversationId;
    }
  }, [activeConversationId, activeConversationKey]);

  useLayoutEffect(() => {
    loadConversations({ silent: true });
  }, [loadConversations]);

  useEffect(() => {
    if (isOpen) {
      loadConversations({ silent: true });
    }
  }, [isOpen, loadConversations]);

  useEffect(() => {
    voiceTranscriptRef.current = transcript;
    if (!isVoiceMode) return;

    setInput(transcript);
    window.requestAnimationFrame(resizeInput);

    if (!transcript.trim()) return;

    if (voiceNoSpeechTimeoutRef.current) {
      clearTimeout(voiceNoSpeechTimeoutRef.current);
      voiceNoSpeechTimeoutRef.current = null;
    }

    if (voiceSilenceTimeoutRef.current) {
      clearTimeout(voiceSilenceTimeoutRef.current);
    }

    voiceSilenceTimeoutRef.current = setTimeout(() => {
      stopVoiceInput({ keepText: true });
    }, 10000);
  }, [isVoiceMode, transcript]);

  useEffect(() => () => clearVoiceTimers(), []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 945px)");
    const updateIsMobile = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", updateIsMobile);

    return () => {
      mediaQuery.removeEventListener("change", updateIsMobile);
    };
  }, []);

  useEffect(() => {
    if (!isExpanded && isConversationSidebarOpen) {
      setIsConversationSidebarOpen(false);
    }
  }, [isConversationSidebarOpen, isExpanded]);

  useEffect(() => {
    if (isOpen && isExpanded) {
      document.body.classList.add('right-sidebar-expanded');
    } else {
      document.body.classList.remove('right-sidebar-expanded');
    }

    return () => {
      document.body.classList.remove('right-sidebar-expanded');
    };
  }, [isOpen, isExpanded]);

  useEffect(() => {
    if (readyPptxFiles.length === 0 && isFileHistoryOpen) {
      setIsFileHistoryOpen(false);
    }
  }, [isFileHistoryOpen, readyPptxFiles.length]);

  useEffect(() => {
    if (!isOpen) {
      void refreshConversationsIfNeeded({ silent: true });
      setIsFileHistoryOpen(false);
      setIsConversationSidebarOpen(false);
      setIsConversationSearchOpen(false);
      if (isVoiceMode || isVoiceListening) {
        stopVoiceInput({ keepText: false });
      }
    }
  }, [isOpen, isVoiceListening, isVoiceMode, refreshConversationsIfNeeded]);

  useEffect(() => {
    if (selectedXmlFile && (isVoiceMode || isVoiceListening)) {
      stopVoiceInput({ keepText: false });
    }
  }, [isVoiceListening, isVoiceMode, selectedXmlFile]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleConversationSidebar = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      setIsConversationSidebarOpen(true);
      loadConversations();
      return;
    }

    setIsConversationSidebarOpen((prev) => {
      const nextIsOpen = !prev;
      if (nextIsOpen) {
        loadConversations();
      }
      return nextIsOpen;
    });
  };

  const handleStop = () => {
    abortControllersRef.current.forEach((requestState) => {
      if (requestState.conversationKey === activeConversationKey) {
        requestState.controller.abort();
      }
    });
    resetTextarea();
  };

  const sendChatMessage = async (messageText) => {
    const currentInput = messageText.trim();
    if (!currentInput || isConversationMessagesLoading || selectedXmlFile) return;
    if (isVoiceMode || isVoiceListening) {
      stopVoiceInput({ keepText: true });
    }
    const userMessage = { role: 'user', content: currentInput };
    const conversationIdAtSend = activeConversationIdRef.current;
    const conversationKeyAtSend = conversationIdAtSend || `pending-${Date.now()}`;
    const requestKey = `request-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    if (!conversationIdAtSend) {
      shouldRefreshConversationsRef.current = true;
      const currentDraftMessages = messagesByConversationRef.current[activeConversationKey] || [];
      const nextCache = {
        ...messagesByConversationRef.current,
        [conversationKeyAtSend]: currentDraftMessages,
      };
      delete nextCache[NEW_CONVERSATION_KEY];
      messagesByConversationRef.current = nextCache;
      setMessagesByConversation(nextCache);
      activeConversationIdRef.current = null;
      activeConversationKeyRef.current = conversationKeyAtSend;
      setActiveConversationKey(conversationKeyAtSend);
    }

    setConversationMessages(conversationKeyAtSend, (prev) => [
      ...prev,
      userMessage,
      { role: 'assistant', content: '', requestKey },
    ]);
    setInput('');
    resetTextarea();
    setConversationLoading(conversationKeyAtSend, requestKey, true);

    const controller = new AbortController();
    abortControllersRef.current.set(requestKey, {
      controller,
      conversationKey: conversationKeyAtSend,
    });
    let responseConversationId = conversationIdAtSend || "";
    let responseConversationKey = conversationKeyAtSend;

    const bindResponseConversation = (nextConversationId) => {
      if (!nextConversationId) return;

      const isResponseConversationActive =
        activeConversationKeyRef.current === responseConversationKey
        || activeConversationKeyRef.current === nextConversationId;

      responseConversationId = nextConversationId;

      if (isResponseConversationActive) {
        activeConversationIdRef.current = nextConversationId;
        setActiveConversationId(nextConversationId);
      }

      if (responseConversationKey === nextConversationId) {
        return;
      }

      renameConversationCacheKey(responseConversationKey, nextConversationId);
      abortControllersRef.current.set(requestKey, {
        controller,
        conversationKey: nextConversationId,
      });
      setConversationLoading(responseConversationKey, requestKey, false);
      setConversationLoading(nextConversationId, requestKey, true);
      responseConversationKey = nextConversationId;
    };

    const applyStreamData = (data) => {
      const streamConversationId = getConversationId(data);

      if (streamConversationId) {
        bindResponseConversation(streamConversationId);
      }

      if (data.type === "delta") {
        return { text: data.text || "", replace: false };
      }

      if (data.type === "session" && (data.conversation_id || data.conversationId)) {
        bindResponseConversation(getConversationId(data));
      } else if (data.type === "final") {
        return { text: data.text || "", replace: true };
      }

      return null;
    };

    try {
      const requestBody = {
        message: buildAgentMessage(userMessage.content),
        ...(conversationIdAtSend ? { conversation_id: conversationIdAtSend } : {}),
      };

      const response = await fetch(buildApiUrl(eliaApiUrl, "agentic/chat"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });
      const headerConversationId = getConversationIdFromHeaders(response.headers);

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      responseConversationId = responseConversationId || headerConversationId || "";
      bindResponseConversation(responseConversationId);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let cumulativeResponse = "";
      let lineBuffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop();

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith('data: ')) continue;

          try {
            const jsonString = trimmedLine.replace('data: ', '');
            if (jsonString === "[DONE]") continue;
            const data = JSON.parse(jsonString);
            const streamResult = applyStreamData(data);

            if (streamResult?.replace) {
              cumulativeResponse = streamResult.text || cumulativeResponse;
            } else if (streamResult?.text) {
              cumulativeResponse += streamResult.text;
            }

            setConversationMessages(responseConversationKey, (prev) => {
              const newMessages = [...prev];
              const assistantIndex = newMessages.findIndex((message) => message.requestKey === requestKey);
              const targetIndex = assistantIndex >= 0 ? assistantIndex : newMessages.length - 1;

              if (newMessages[targetIndex]?.role === 'assistant') {
                newMessages[targetIndex] = {
                  ...newMessages[targetIndex],
                  content: cumulativeResponse
                };
              }
              return newMessages;
            });
          } catch (e) {
            console.error("Erreur lors du parsing du chunk JSON", e);
          }
        }
      }

      const trailingLine = lineBuffer.trim();
      if (trailingLine.startsWith('data: ')) {
        try {
          const jsonString = trailingLine.replace('data: ', '');
          if (jsonString !== "[DONE]") {
            const data = JSON.parse(jsonString);
            const streamResult = applyStreamData(data);

            if (streamResult?.replace) {
              cumulativeResponse = streamResult.text || cumulativeResponse;
            } else if (streamResult?.text) {
              cumulativeResponse += streamResult.text;
            }

            setConversationMessages(responseConversationKey, (prev) => {
              const newMessages = [...prev];
              const assistantIndex = newMessages.findIndex((message) => message.requestKey === requestKey);
              const targetIndex = assistantIndex >= 0 ? assistantIndex : newMessages.length - 1;

              if (newMessages[targetIndex]?.role === 'assistant') {
                newMessages[targetIndex] = {
                  ...newMessages[targetIndex],
                  content: cumulativeResponse
                };
              }
              return newMessages;
            });
          }
        } catch (e) {
          console.error("Erreur lors du parsing du dernier chunk JSON", e);
        }
      }

      if (responseConversationId) {
        bindResponseConversation(responseConversationId);

        if (!conversationIdAtSend) {
          shouldRefreshConversationsRef.current = true;
        }

        setConversations((prev) => {
          hasLoadedConversationsRef.current = true;
          if (prev.some((conversation) => conversation.id === responseConversationId)) return prev;

          return [
            {
              id: responseConversationId,
              title: userMessage.content,
              updatedAt: new Date().toISOString(),
            },
            ...prev,
          ];
        });
      }
    } catch (error) {
      if (error.name === "AbortError") {
        setConversationMessages(responseConversationKey, (prev) => {
          const newMessages = [...prev];
          const assistantIndex = newMessages.findIndex((message) => message.requestKey === requestKey);
          if (assistantIndex >= 0 && newMessages[assistantIndex]?.content === '') {
            newMessages.splice(assistantIndex, 1);
          }
          return newMessages;
        });
      } else {
        console.error("Erreur API:", error);
        setConversationMessages(responseConversationKey, (prev) => {
          const newMessages = [...prev];
          const assistantIndex = newMessages.findIndex((message) => message.requestKey === requestKey);
          const targetIndex = assistantIndex >= 0 ? assistantIndex : newMessages.length - 1;
          if (newMessages.length > 0 && newMessages[targetIndex]?.role === 'assistant') {
            newMessages[targetIndex] = {
              ...newMessages[targetIndex],
              role: 'assistant',
              content: 'Désolé, une erreur est survenue. Veuillez réessayer plus tard.'
            };
          }
          return newMessages;
        });
      }
    } finally {
      setConversationLoading(conversationKeyAtSend, requestKey, false);
      if (responseConversationId) {
        setConversationLoading(responseConversationId, requestKey, false);
      }
      abortControllersRef.current.delete(requestKey);
      resetTextarea();
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (voiceSubmitGuardRef.current) return;
    await sendChatMessage(input);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    window.requestAnimationFrame(resizeInput);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isVoiceMode || isVoiceListening) {
        stopVoiceInput({ keepText: Boolean(voiceTranscriptRef.current.trim()) });
        return;
      }
      handleSend(e);
    }
  };

  const resetTextarea = () => {
    const textarea = inputRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.overflowY = "hidden";
  };

  const prefillWelcomeQuestion = (prompt) => {
    setInput(prompt);
    window.requestAnimationFrame(() => {
      resizeInput();
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(prompt.length, prompt.length);
    });
  };

  const handleWelcomeQuestionClick = (question) => {
    if (isLoading || isConversationMessagesLoading || isConfigUploading || selectedXmlFile) return;

    if (question.action === "prefill") {
      prefillWelcomeQuestion(question.prompt);
      return;
    }

    sendChatMessage(question.prompt);
  };

  const handleVoiceToggle = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!browserSupportsSpeechRecognition || isConversationMessagesLoading || selectedXmlFile) return;

    if (isVoiceMode || isVoiceListening) {
      voiceSubmitGuardRef.current = true;
      stopVoiceInput({ keepText: Boolean(voiceTranscriptRef.current.trim()) });
      window.setTimeout(() => {
        voiceSubmitGuardRef.current = false;
      }, 150);
      return;
    }

    if (input.trim()) return;

    resetTranscript();
    voiceTranscriptRef.current = "";
    setIsVoiceMode(true);
    setInput("");
    resetTextarea();
    SpeechRecognition.startListening({
      continuous: true,
      language: "fr-FR",
    });

    clearVoiceTimers();
    voiceNoSpeechTimeoutRef.current = setTimeout(() => {
      stopVoiceInput({ keepText: false });
    }, 15000);
  };

  const hasVoiceDraft = Boolean((transcript || input).trim());

  const handlePowerPointDownload = async (configurationName, sourceContent) => {
    const downloadKey = getStableKey(`${configurationName || "configuration"}::${sourceContent}`);
    const currentDownload = pptxDownloads[downloadKey];

    if (currentDownload?.url) {
      downloadFile(currentDownload.url);
      return;
    }

    if (currentDownload?.isLoading || pptxInFlightRef.current.has(downloadKey)) {
      return;
    }

    pptxInFlightRef.current.add(downloadKey);
    setPptxDownloads((prev) => ({
      ...prev,
      [downloadKey]: { ...prev[downloadKey], isLoading: true, error: "" },
    }));

    try {
      const requestMessage = buildSummaryRequest(configurationName, sourceContent);

      const response = await fetch(buildApiUrl(eliaApiUrl, "agentic/chat"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: requestMessage,
        }),
      });

      if (!response.ok || !response.body) {
        const errorBody = await response.text().catch(() => "");

        const backendFailureMessage = getBackendFailureMessage(errorBody);
        if (backendFailureMessage) {
          throw new Error(backendFailureMessage);
        }

        if (response.status === 401) {
          throw new Error("Session non autorisee pour generer le PowerPoint.");
        }

        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let cumulativeResponse = "";
      let rawStreamResponse = "";
      let lineBuffer = "";
      let pendingJsonBuffer = "";
      let pptxUrl = "";
      let chunkCount = 0;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        chunkCount += 1;

        const decodedChunk = decoder.decode(value, { stream: true });
        rawStreamResponse += decodedChunk;
        pptxUrl = getPptxLink(rawStreamResponse);
        if (pptxUrl) {
          await reader.cancel();
          break;
        }

        lineBuffer += decodedChunk;
        const lines = lineBuffer.split("\n");
        lineBuffer = lines.pop();

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;

          const dataPayload = trimmedLine.replace("data: ", "");
          if (dataPayload === "[DONE]") {
            continue;
          }

          try {
            const jsonCandidate = pendingJsonBuffer ? `${pendingJsonBuffer}${dataPayload}` : dataPayload;
            const data = JSON.parse(jsonCandidate);
            pendingJsonBuffer = "";
            if (data.type === "delta") {
              cumulativeResponse += data.text;
            } else if (data.type === "final") {
              cumulativeResponse = data.text;
            }

            pptxUrl = getPptxLink(cumulativeResponse);
            if (pptxUrl) {
            }
          } catch (parseError) {
            const jsonCandidate = pendingJsonBuffer ? `${pendingJsonBuffer}${dataPayload}` : dataPayload;
            pptxUrl = getPptxLink(jsonCandidate) || getPptxLink(rawStreamResponse);

            if (pptxUrl) {
              break;
            }

            if (isRecoverableJsonParseError(parseError)) {
              pendingJsonBuffer = jsonCandidate;
            } else {
              console.error("Erreur lors du parsing du lien PPTX", parseError);
              pendingJsonBuffer = "";
            }
          }
        }

        if (pptxUrl) {
          await reader.cancel();
          break;
        }
      }

      if (!pptxUrl && lineBuffer.trim().startsWith("data: ")) {
        try {
          const dataPayload = lineBuffer.trim().replace("data: ", "");
          const data = JSON.parse(pendingJsonBuffer ? `${pendingJsonBuffer}${dataPayload}` : dataPayload);
          if (data.type === "delta") {
            cumulativeResponse += data.text;
          } else if (data.type === "final") {
            cumulativeResponse = data.text;
          }
        } catch (parseError) {
          console.error("Erreur lors du parsing final du lien PPTX", parseError);
        }
      }

      if (!pptxUrl) {
        pptxUrl = getPptxLink(cumulativeResponse) || getPptxLink(rawStreamResponse) || getPptxLink(pendingJsonBuffer);
      }

      if (!pptxUrl) {
        const backendFailureMessage = getBackendFailureMessage(rawStreamResponse) || getBackendFailureMessage(cumulativeResponse);
        if (backendFailureMessage) {
          throw new Error(backendFailureMessage);
        }

        throw new Error("Lien PPTX introuvable");
      }

      setPptxDownloads((prev) => ({
        ...prev,
        [downloadKey]: {
          isLoading: false,
          url: pptxUrl,
          error: "",
          name: getFileNameFromUrl(pptxUrl),
          configurationName: configurationName || "Configuration",
          createdAt: new Date().toISOString(),
        },
      }));
      downloadFile(pptxUrl);
    } catch (downloadError) {
      console.error("Erreur téléchargement PowerPoint:", downloadError);
      setPptxDownloads((prev) => ({
        ...prev,
        [downloadKey]: {
          ...prev[downloadKey],
          isLoading: false,
          error: downloadError?.message || "Impossible de générer le PowerPoint.",
        },
      }));
    } finally {
      pptxInFlightRef.current.delete(downloadKey);
    }
  };

  const handleImportClick = () => {
    setIsImportMenuOpen((prev) => !prev);
  };

  const handleCheckFiles = () => {
    if (readyPptxFiles.length === 0) return;
    setIsImportMenuOpen(false);
    setIsFileHistoryOpen((prev) => !prev);
  };

  const handleXmlOptionClick = () => {
    setIsImportMenuOpen(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    uploadXmlConfig(file);
    e.target.value = "";
  };

  const uploadXmlConfig = async (file) => {
    const haloId = user?.haloId ?? user?.holdingId;
    const entrepriseId = user?.entrepriseId;

    if (!haloId || !entrepriseId) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Impossible d'importer le XML: `halo_id` ou `entreprise_id` manquant." }
      ]);
      return;
    }

    setIsConfigUploading(true);
    setSelectedXmlFile(file);

    try {
      const formData = new FormData();
      formData.append("xml_file", file);
      formData.append("halo_id", String(haloId));
      formData.append("entreprise_id", String(entrepriseId));

      const response = await fetch("https://elia.elit-technologies.fr/agentic/config/ingest", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `Configuration XML importée: **${file.name}**` }
      ]);
    } catch (error) {
      console.error("Erreur import XML:", error);
      setSelectedXmlFile(null);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Échec de l'import de la configuration XML. Veuillez réessayer." }
      ]);
    } finally {
      setIsConfigUploading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isImportMenuOpen) return;
      if (importMenuRef.current && !importMenuRef.current.contains(event.target)) {
        setIsImportMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isImportMenuOpen]);

  const isConversationPanelVisible = isConversationSidebarOpen && isExpanded;
  const closingLayout = !isOpen ? lastOpenLayoutRef.current : null;
  const displayedIsExpanded = isExpanded || Boolean(closingLayout?.isExpanded);
  const displayedConversationPanelVisible = isConversationPanelVisible || Boolean(closingLayout?.isConversationPanelVisible);
  const visibleMessages = messages.filter((message) =>
    !(message.role === "user" && isInternalPptxRequest(message.content))
  );
  const isSearchMode = isConversationSearchOpen;
  const isWelcomeMode = !isSearchMode && !isConversationMessagesLoading && visibleMessages.length === 0;
  const welcomeName = user?.firstName || user?.firstname || user?.name || "";
  const welcomeTitle = `${getWelcomeGreeting()}${welcomeName ? `, ${welcomeName}` : ""}`;
  const inputPlaceholder = isWelcomeMode ? "Comment puis-je vous aider ?" : "Posez votre question...";
  const isWelcomeQuestionDisabled = isLoading || isConversationMessagesLoading || isConfigUploading || Boolean(selectedXmlFile);

  useEffect(() => {
    if (!isOpen) return;

    lastOpenLayoutRef.current = {
      isExpanded,
      isConversationPanelVisible,
    };
  }, [isOpen, isExpanded, isConversationPanelVisible]);

  return (
    <div
      className={`right-sidebar ${isOpen ? 'open' : ''} ${displayedIsExpanded ? 'expanded' : ''} ${isLeftSidebarOpen ? 'left-sidebar-open' : ''} ${displayedConversationPanelVisible ? 'conversations-open' : ''} ${isWelcomeMode ? 'welcome-mode' : ''}`}
    >
      <div className="right-sidebar-header">
        <div className="header-left">
          <button
              onClick={toggleExpand}
              className="expand-btn right-sidebar-expand-btn"
              title={isExpanded ? "Réduire" : "Agrandir"}
            >
              <svg className="right-sidebar-expand-icon" width="103" height="93" viewBox="0 0 103 93" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M89.9913 4H13.0087C8.03811 4 4 8.02973 4 13.0003V79.9997C4 84.9703 8.03812 89 13.0087 89H89.9913C94.9619 89 99 84.9703 99 79.9997V13.0003C99 8.02973 94.9619 4 89.9913 4Z" stroke="black" strokeWidth="8" />
                <path d="M28 3V88" stroke="black" strokeWidth="8" />
              </svg>
          </button>
          <button
              type="button"
              onClick={toggleConversationSidebar}
              className={`expand-btn right-sidebar-conversations-btn ${isConversationPanelVisible ? "open" : ""}`}
              title="Conversations"
              aria-label="Afficher les conversations"
              aria-expanded={isConversationPanelVisible}
            >
              <svg width="17" height="15" viewBox="0 0 18 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.33081 9.33056C3.50648 11.2412 0.0936456 12.1153 0.602988 12.1624C0.602988 12.1624 6.71829 12.275 7.83661 12.1271" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                <path d="M7.88988 12.6774C9.16028 12.511 10.3441 11.9424 11.268 11.0547C12.1919 10.167 12.8073 9.00689 13.0243 7.74415C13.2413 6.4814 13.0483 5.18237 12.4737 4.03721C11.8991 2.89205 10.9729 1.9609 9.83091 1.38008C8.68888 0.799261 7.39091 0.599267 6.12701 0.809378C4.86311 1.01949 3.69966 1.62867 2.80697 2.54775C1.91429 3.46682 1.33925 4.64752 1.16603 5.917C0.992821 7.18648 1.23053 8.47808 1.84437 9.60271L2.81797 9.0713C2.31761 8.15458 2.12384 7.10175 2.26504 6.06695C2.40623 5.03215 2.87496 4.06972 3.60262 3.32055C4.33029 2.57138 5.27866 2.07482 6.30891 1.90355C7.33916 1.73228 8.39718 1.8953 9.32809 2.36875C10.259 2.8422 11.0139 3.60121 11.4823 4.53467C11.9507 5.46813 12.108 6.52702 11.9311 7.55633C11.7543 8.58563 11.2526 9.5313 10.4995 10.2549C9.74639 10.9785 8.78143 11.442 7.74588 11.5776L7.88988 12.6774Z" fill="currentColor" />
                <path d="M15.7493 11.1894C14.5736 13.1001 17.9864 13.9742 17.4771 14.0213C17.4771 14.0213 11.3618 14.1338 10.2435 13.986" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                <path d="M5.83448 11.6913C6.10393 12.137 6.43189 12.5484 6.81207 12.9137C7.73597 13.8014 8.91977 14.37 10.1902 14.5364L10.3342 13.4366C9.29861 13.301 8.33366 12.8375 7.58055 12.1139C7.42236 11.9619 7.27526 11.8001 7.13993 11.6298L5.83448 11.6913Z" fill="currentColor" />
                <path d="M16.9144 7.77588C16.7412 6.5064 16.1661 5.3257 15.2735 4.40663C14.3808 3.48755 13.2173 2.87837 11.9534 2.66826C11.2621 2.55333 10.5606 2.5611 9.87999 2.68678C10.4265 3.02052 10.6993 3.26578 10.976 3.69621C11.2412 3.69656 11.5073 3.7185 11.7715 3.76243C12.8018 3.9337 13.7502 4.43026 14.4778 5.17943C15.2055 5.9286 15.6742 6.89103 15.8154 7.92583C15.9566 8.96063 15.7628 10.0135 15.2625 10.9302L16.2361 11.4616C16.8499 10.337 17.0876 9.04536 16.9144 7.77588Z" fill="currentColor" />
              </svg>
          </button>
          <h3>
            ELIA
          </h3>
        </div>
        <button onClick={onClose} className="close-btn"><FontAwesomeIcon icon={faClose} /></button>
      </div>

      <button
        type="button"
        className={`elia-file-history-overlay ${isFileHistoryOpen ? "open" : ""}`}
        onClick={() => setIsFileHistoryOpen(false)}
        aria-label="Fermer l'historique des fichiers"
        tabIndex={isFileHistoryOpen ? 0 : -1}
      />

      <aside
        className={`elia-file-history ${isFileHistoryOpen ? "open" : ""}`}
        aria-label="Historique des fichiers disponibles"
        aria-hidden={!isFileHistoryOpen}
      >
        <div className="elia-file-history-header">
          <div>
            <span className="elia-file-history-kicker">Fichiers</span>
            <h3>Historique disponible</h3>
          </div>
          <button
            type="button"
            className="elia-file-history-close"
            onClick={() => setIsFileHistoryOpen(false)}
            aria-label="Fermer l'historique des fichiers"
            tabIndex={isFileHistoryOpen ? 0 : -1}
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        <div className="elia-file-history-list">
          <h4>Contenu</h4>
          {readyPptxFiles.map((file) => (
            <div key={file.id} className="elia-file-history-item">
              <div className="elia-file-history-preview" aria-hidden="true">
                <div className="elia-file-history-preview-badge">
                  <FontAwesomeIcon icon={faFilePowerpoint} />
                  <span>PPTX</span>
                </div>
                <div className="elia-file-history-preview-slide">
                  <span>{file.configurationName}</span>
                  <strong>{file.name}</strong>
                  <i />
                  <i />
                </div>
              </div>
              <div className="elia-file-history-info">
                <strong title={file.name}>{file.name}</strong>
                <span title={file.configurationName}>{file.configurationName}</span>
                {file.createdAt && <em>{formatHistoryDate(file.createdAt)}</em>}
              </div>
              <div className="elia-file-history-actions">
                <span>FICHIER</span>
                <button
                  type="button"
                  className="elia-file-history-download"
                  onClick={() => downloadFile(file.url)}
                  aria-label={`Telecharger ${file.name}`}
                  title="Telecharger"
                  tabIndex={isFileHistoryOpen ? 0 : -1}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      <div className="right-sidebar-content">
        <aside
          className={`elia-conversations ${displayedConversationPanelVisible ? "open" : ""}`}
          aria-label="Conversations ElIA"
          aria-hidden={!isConversationPanelVisible}
        >

          <div className="elia-conversations-actions">
            <button
              type="button"
              className="elia-conversations-action add"
              onClick={startNewConversation}
              title="Nouvelle conversation"
              aria-label="Nouvelle conversation"
              tabIndex={isConversationPanelVisible ? 0 : -1}
            >
              <AddIcon fontSize="large" />
              <span>Nouvelle conversation</span>
            </button>
            <button
              type="button"
              className="elia-conversations-action"
              onClick={openConversationSearch}
              title="Rechercher une conversations"
              aria-label="Rechercher une conversations"
              tabIndex={isConversationPanelVisible ? 0 : -1}
            >
              <SearchIcon className="search-icon" fontSize="large" />
              <span>Rechercher une conversations</span>
            </button>
          </div>

          <div className="elia-conversations-header">
            <h4>Récents</h4>
          </div>

          <div className="elia-conversations-list">
            {isConversationsLoading ? (
              <div className="elia-conversations-loading" aria-label="Chargement des conversations">
                {[0, 1, 2, 3].map((item) => (
                  <span key={item} className="elia-conversations-loading-bar" />
                ))}
              </div>
            ) : recentConversations.length === 0 ? (
              <div className="elia-conversations-empty">Aucune conversation</div>
            ) : (
              recentConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  className={`elia-conversations-item ${activeConversationId === conversation.id ? "active" : ""}`}
                  onClick={() => selectConversation(conversation.id)}
                  title={conversation.title}
                  tabIndex={isConversationPanelVisible ? 0 : -1}
                >
                  <span>{conversation.title}</span>
                </button>
              ))
            )}
          </div>
        </aside>

        <div className="right-sidebar-chat-panel">
          {isSearchMode ? (
            <section className="elia-chat-search-view" aria-label="Rechercher des conversations">
              <div className="elia-chat-search-toolbar">
                <div>
                  <h2>Conversations</h2>
                </div>
                <div className="elia-chat-search-toolbar-actions">
                  <button
                    type="button"
                    className="elia-chat-search-new"
                    onClick={startNewConversation}
                  >
                    <span>Nouvelle conversation</span>
                  </button>
                  <button
                    type="button"
                    className="elia-chat-search-close"
                    onClick={closeConversationSearch}
                    aria-label="Fermer la recherche"
                    title="Fermer"
                  >
                    <FontAwesomeIcon icon={faClose} />
                  </button>
                </div>
              </div>

              <div className="elia-chat-search-head">
                <SearchIcon fontSize="large" />
                <input
                  type="search"
                  value={conversationSearchQuery}
                  onChange={(event) => setConversationSearchQuery(event.target.value)}
                  placeholder="Rechercher des conversations..."
                  aria-label="Rechercher des conversations"
                  autoFocus
                />
              </div>

              <div className="elia-chat-search-body">
                {isConversationsLoading ? (
                  <div className="elia-conversations-loading" aria-label="Chargement des conversations">
                    {[0, 1, 2, 3, 4].map((item) => (
                      <span key={item} className="elia-conversations-loading-bar" />
                    ))}
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="elia-chat-search-empty">Aucune conversation trouvée</div>
                ) : (
                  filteredConversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      type="button"
                      className={`elia-chat-search-item ${activeConversationId === conversation.id ? "active" : ""}`}
                      onClick={() => selectConversation(conversation.id)}
                      title={conversation.title}
                    >
                      <span>{conversation.title}</span>
                      {conversation.updatedAt && <time>{formatConversationDate(conversation.updatedAt)}</time>}
                    </button>
                  ))
                )}
              </div>
            </section>
          ) : (
            <>
              {isWelcomeMode && (
                <section className="elia-chat-welcome" aria-label="Accueil ElIA">
                  <h2>
                    <img src="/assets/icon_elia.svg" alt="" aria-hidden="true" />
                    <span>{welcomeTitle}</span>
                  </h2>
                </section>
              )}

              <div className="chat-messages">
                {isConversationMessagesLoading && (
                  <div className="conversation-loading-skeleton" aria-label="Chargement de la conversation">
                    <span className="assistant wide" />
                    <span className="assistant medium" />
                    <span className="user short" />
                    <span className="assistant large" />
                  </div>
                )}
                {visibleMessages.map((msg, index) => {
                  const isAssistantLoading = Boolean(
                    msg.requestKey && loadingConversationKeys[activeConversationKey]?.[msg.requestKey]
                  );
                  const configurationName = msg.role === 'assistant' ? getConfigurationName(msg.content) : "";
                  const pptxDownloadKey = configurationName ? getStableKey(`${configurationName || "configuration"}::${msg.content}`) : "";
                  const pptxDownload = pptxDownloads[pptxDownloadKey];
                  const messageParts = msg.role === 'assistant'
                    ? parseMessageContent(msg.content)
                    : [{ type: 'markdown', content: msg.content }];
                  const hasTableGraph = messageParts.some((part) => part.type === 'tableGraph');
                  return (
                    <div key={index} className={`message ${msg.role}`}>
                      {msg.role === 'assistant' && (
                        <div className="message-avatar">
                          <img className={`${theme === 'light' ? 'avatar-elia-light' : 'avatar-elia-dark'}`} src="/assets/elia_persona.png" alt="ElIA" />
                        </div>
                      )}
                      <div className={`message-bubble ${hasTableGraph ? 'has-table-graph' : ''}`}>
                        {renderMessageParts(messageParts)}
                        {isAssistantLoading && (
                          <div className="loading-indicator">
                            <div className="orbit-loader">
                              <FontAwesomeIcon icon={faCircle} className="dot dot-1" />
                              <FontAwesomeIcon icon={faCircle} className="dot dot-2" />
                            </div>
                          </div>
                        )}
                        {configurationName && msg.content && !isAssistantLoading && (
                          <EliaSummaryAction
                            configurationName={configurationName}
                            isLoading={Boolean(pptxDownload?.isLoading)}
                            hasDownload={Boolean(pptxDownload?.url)}
                            error={pptxDownload?.error}
                            onDownload={() => handlePowerPointDownload(configurationName, msg.content)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
                {activeConversationId && !isSearchMode && (
                  <div className="elia-chat-warning" role="status">
                    Elia est une IA et peut faire des erreurs. Veuillez vérifier les réponses.
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSend} className="chat-input-area">
                <div className="chat-composer">
                  <div className="input-wrapper">
                    <textarea
                      id="chat-input"
                      name="chat-message"
                      ref={inputRef}
                      value={input}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder={inputPlaceholder}
                      aria-label="Message pour ElIA"
                      rows={1}
                    />
                  </div>

                  <div className="composer-actions" ref={importMenuRef}>
                    {/* <button
                type="button"
                className={`composer-plus-btn ${isImportMenuOpen ? "open" : ""}`}
                onClick={handleImportClick}
                title="Ouvrir les options d'import"
                aria-label="Ouvrir les options d'import"
                disabled={isLoading || isConfigUploading}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button> */}

                    {readyPptxFiles.length > 0 && (
                      <button
                        type="button"
                        className={`composer-plus-btn ${isFileHistoryOpen ? "open" : ""}`}
                        onClick={handleCheckFiles}
                        title="Afficher les fichiers disponibles"
                        aria-label="Afficher les fichiers disponibles"
                        disabled={isLoading || isConversationMessagesLoading || isConfigUploading}
                      >
                        <FontAwesomeIcon icon={faFileAlt} />
                      </button>
                    )}

                    {selectedXmlFile && (
                      <span className="selected-xml-name" title={selectedXmlFile.name}>
                        {selectedXmlFile.name}
                      </span>
                    )}

                    {isVoiceMode ? (
                      <button
                        type="button"
                        onClick={handleVoiceToggle}
                        className="input-icon-btn send-btn voice-btn listening"
                        title={hasVoiceDraft ? "Arreter la dictee vocale" : "Annuler la dictee vocale"}
                        aria-label={hasVoiceDraft ? "Arreter la dictee vocale" : "Annuler la dictee vocale"}
                      >
                        <span className="voice-listening-dots" aria-hidden="true">
                          <i />
                          <i />
                          <i />
                        </span>
                        <span className="voice-listening-label">{hasVoiceDraft ? "Arreter" : "Annuler"}</span>
                      </button>
                    ) : input.trim() && !selectedXmlFile && !isConversationMessagesLoading ? (
                      <button type="submit" className="input-icon-btn send-btn">
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                    ) : isLoading ? (
                      <button type="button" onClick={handleStop} className="input-icon-btn send-btn stop">
                        <FontAwesomeIcon icon={faCircleStop} />
                      </button>
                    ) : browserSupportsSpeechRecognition && !selectedXmlFile && !isConversationMessagesLoading ? (
                      <button
                        type="button"
                        onClick={handleVoiceToggle}
                        className="input-icon-btn send-btn voice-btn"
                        title="Dicter un message"
                        aria-label="Dicter un message"
                      >
                        <FontAwesomeIcon icon={faMicrophone} />
                      </button>
                    ) : null}

                    {/* {isImportMenuOpen && (
                <div className="import-menu">
                  <button
                    type="button"
                    className="import-menu-item"
                    onClick={handleXmlOptionClick}
                    disabled={isLoading || isConfigUploading}
                  >
                    <FontAwesomeIcon icon={faPaperclip} />
                    <span>{isConfigUploading ? "Import en cours..." : "Ajouter votre configuration /.xml"}</span>
                  </button>
                </div>
              )} */}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xml,text/xml,application/xml"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </div>
              </form>

              {isWelcomeMode && (
                <div className="elia-welcome-prompts" aria-label="Questions suggérées">
                  {WELCOME_BENCHMARK_QUESTIONS.map((question) => (
                    <button
                      key={question.label}
                      type="button"
                      className={`elia-welcome-prompt-chip ${question.action === "prefill" ? "prefill" : ""}`}
                      onClick={() => handleWelcomeQuestionClick(question)}
                      title={question.label}
                      disabled={isWelcomeQuestionDisabled}
                    >
                      <FontAwesomeIcon icon={question.icon} />
                      <span>{question.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RightSidebar;
